import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendSampleTestInformation,
	FrontendUserInfo,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";

import { Divider } from "@mui/material";
import SampleCard from "./sampleCard";
import DeleteSampleConfirmModal from "../../../common-components/modals/deleteSampleConfirmModal";
import { TestHierarchyNodeType } from "node-library/dist/constants";
import { CustomCheckbox } from "../../../common-components/inputs";
import { getEnabledHierarchyNodeTree } from "../../../helpers/testHierarchyHelper";
import CreateOrUpdateSpecificationReadingDrawer from "../../../common-components/drawers/createOrUpdateSpecificationReadingDrawer";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;

	isImportMode?: boolean;
	orderTestCategory: FrontendCompanyTestCategory;

	sampleInstances: Array<FrontendSampleInstance>;

	deleteSample: (sampleId: string) => void;
	goToUpdateSample: (sampleToUpdate: FrontendSampleInstance) => void;
	isReadOnlyTable?: boolean;
	isDetailsPageTable?: boolean;
	duplicateSample: (sampleToUpdate: FrontendSampleInstance) => void;
	isInNestedCard?: boolean;
	openEditExpectationModal: (instanceToUpdate: FrontendSampleInstance) => void;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function SamplePreviewTable(props: Props) {
	const { t: translation } = useTranslation();

	const {
		company,
		setCompany,
		user,
		sampleInstances,
		deleteSample,
		goToUpdateSample,
		isReadOnlyTable,
		isDetailsPageTable,
		duplicateSample,
		isInNestedCard,
		orderTestCategory,
		openEditExpectationModal,
		specDirectories,
		updateSpecification,
	} = props;

	const hierarchyNodeParentMap = new Map<
		string,
		FrontendCompanyTestHierarchyNode[]
	>();

	orderTestCategory.hierarchyNodes.forEach((node) => {
		const parentId = node.parentNodeId || "root";
		if (!hierarchyNodeParentMap.has(parentId))
			hierarchyNodeParentMap.set(parentId, [node]);
		else {
			hierarchyNodeParentMap.get(parentId)!.push(node);
		}
	});

	//confirm modals
	const [deleteSampleConfirmOpen, setDeleteSampleConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {},
	);

	const categoryTestGroupNodes: Array<FrontendCompanyTestHierarchyNode> =
		orderTestCategory.hierarchyNodes.filter((node) => {
			return node.nodeType === TestHierarchyNodeType.TEST_GROUP;
		});

	const [filterByTestGroupIds, setFilterByTestGroupIds] = useState<
		Array<string>
	>(
		categoryTestGroupNodes.map((node) => {
			return node.nodeId;
		}),
	);

	const [readingDrawerOpen, setReadingDrawerOpen] = useState<boolean>(false);
	const [selectedSpecification, setSelectedSpecification] = useState<
		FrontendSampleSpecification | undefined
	>();

	function openSpecReadingDrawer(spec: FrontendSampleSpecification) {
		setSelectedSpecification(spec);
		setReadingDrawerOpen(true);
	}

	const headerRow: React.ReactNode = (
		<div
			className={`sample-preview-list-row sample-preview-header-row ${
				isReadOnlyTable ? "read-only" : ""
			} ${isDetailsPageTable ? "order-details" : ""}`}
		>
			<div className="sample-preview-header-column sample-preview-number-column">
				{isDetailsPageTable ? translation("SampleId") : "No."}
				<Divider orientation="vertical" flexItem />
			</div>
			<div className="sample-preview-header-column">
				{translation("SampleInformation")}
				<Divider orientation="vertical" flexItem />
			</div>
			<div className="sample-preview-header-column ">
				{translation("TestsAndTurnaroundTime")}
				<Divider orientation="vertical" flexItem />
			</div>
			{!isReadOnlyTable ? (
				<div
					className="sample-preview-header-column"
					style={{ justifyContent: "space-around" }}
				>
					{translation("Actions")}
					<Divider orientation="vertical" flexItem />
				</div>
			) : (
				<></>
			)}
			<div
				className="sample-preview-header-column"
				style={{ justifyContent: "space-around" }}
			>
				{translation("Show")}
				<div className="placeholder"></div>
			</div>
		</div>
	);

	const toggleCheckboxOption = (testGroupId: string) => {
		setFilterByTestGroupIds((prev) => {
			// Check if the batchId already exists
			const exists = prev.includes(testGroupId);

			// Create a new array with batchId added or removed
			const updatedTestGroupIds = exists
				? prev.filter((id) => id !== testGroupId)
				: [...prev, testGroupId];

			// Return updated state
			return updatedTestGroupIds;
		});
	};

	const testGroupFilterFields: React.ReactNode =
		isDetailsPageTable && categoryTestGroupNodes.length > 1 ? (
			<div className="test-group-option-filter-row typography-body-02">
				{`${translation("Filterby")}:`}
				{categoryTestGroupNodes.map((node) => {
					const selected: boolean = filterByTestGroupIds.includes(node.nodeId);
					return (
						<div key={node.nodeId}>
							<CustomCheckbox
								className={`test-group-option-filter-checkbox ${
									selected && "test-group-option-filter-checkbox-checked"
								}`}
								label={node.nodeName}
								checked={selected}
								setChecked={() => toggleCheckboxOption(node.nodeId)}
							/>
						</div>
					);
				})}
			</div>
		) : null;

	const sampleRows: Array<React.ReactNode> = sampleInstances
		? sampleInstances.map(
				(sampleInstance: FrontendSampleInstance, index: number) => {
					const selectedNodeIds: Array<string> =
						sampleInstance.sampleTestInfo?.selectedTests.map((info) => {
							return info.selectedNodeId;
						}) as Array<string>;
					const instanceHierarchyTree: TestHierarchyTreeNode =
						getEnabledHierarchyNodeTree(
							selectedNodeIds,
							hierarchyNodeParentMap,
							filterByTestGroupIds,
						);
					if (
						instanceHierarchyTree.children.length == 0 &&
						!sampleInstance.sampleTestInfo?.additionalTest
					) {
						return null;
					}

					return (
						<SampleCard
							company={company}
							user={props.user}
							isImportMode={props.isImportMode}
							hierarchyNodeParentMap={hierarchyNodeParentMap}
							instanceHierarchyTree={instanceHierarchyTree}
							orderTestCategory={orderTestCategory}
							key={index}
							sampleInstance={sampleInstance}
							index={index}
							deleteSample={() => {
								setProceedAction(
									() => () => deleteSample(sampleInstance.sampleInstanceId),
								);
								setDeleteSampleConfirmOpen(true);
							}}
							goToUpdateSample={() => goToUpdateSample(sampleInstance)}
							isReadOnlyTable={isReadOnlyTable}
							isDetailsPageTable={isDetailsPageTable}
							duplicateSample={() => duplicateSample(sampleInstance)}
							openEditExpectationModal={() => {
								openEditExpectationModal(sampleInstance);
							}}
							openSpecReadingDrawer={() => {
								if (sampleInstance.specification) {
									openSpecReadingDrawer(sampleInstance.specification);
								}
							}}
						/>
					);
				},
			)
		: [];

	return (
		<>
			<div
				className={`sample-preview-table-container ${
					isInNestedCard ? `sample-preview-table-nested` : ``
				}`}
			>
				{testGroupFilterFields}

				{headerRow}

				{sampleRows}
			</div>
			<DeleteSampleConfirmModal
				open={deleteSampleConfirmOpen}
				setOpen={setDeleteSampleConfirmOpen}
				proceedAction={proceedAction}
			></DeleteSampleConfirmModal>

			{readingDrawerOpen && selectedSpecification && (
				<CreateOrUpdateSpecificationReadingDrawer
					company={company}
					setCompany={setCompany}
					testCategory={orderTestCategory}
					// Derive matrix from the sample associated with the spec
					selectedMatrix={
						orderTestCategory.sampleMatrices.find(
							(m) => m.matrixId === selectedSpecification.matrixId,
						) as FrontendCompanySampleMatrix
					}
					selectedSpecFile={selectedSpecification}
					user={user}
					open={readingDrawerOpen}
					setOpen={setReadingDrawerOpen}
					specDirectories={specDirectories}
					updateSpecification={updateSpecification}
				/>
			)}
		</>
	);
}
